import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { Search, Plus, UserCog, Mail, Phone, Edit2, Trash2, MessageCircle, PhoneCall, ChevronRight, X, Building } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import {
  safeAsync, Badge, emptyMsg, neuShadow, neuShadowSm,
  btnPrimary, neuShadowInset,
} from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

/* ═══════════════════════════════════════════════════════════════════════════
   UsersTab — Dedicated tab for GHL Users (staff/team) separate from clients
   • Displays contacts with contact_type='partner' (imported GHL users)
   • Same alphabetical accordion layout as ContactsTab
   • Distinct color scheme (#7C3AED purple) to visually differentiate
   ═══════════════════════════════════════════════════════════════════════════ */

const USER_COLOR = '#7C3AED'; // Purple for Users section

export default function UsersTab({
  contacts, loadData,
  setEditContact, setShowEditContact,
  setShowSendModal, setSendChannel, setSendResult, setSendPrefill,
  canCreate, setShowAddContact,
}) {
  const [search, setSearch] = useState('');
  const [expandedLetters, setExpandedLetters] = useState(new Set());
  const scrollContainerRef = useRef(null);

  // ── Filter to only 'partner' type contacts (GHL users) ────────────────
  const users = useMemo(() => {
    let filtered = contacts.filter(c => c.contact_type === 'partner');
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(c =>
        matchesNameQuery(
          [c.first_name, c.last_name, c.organization, c.email, c.phone, c.title], q)
      );
    }
    return filtered;
  }, [contacts, search]);

  // ── Group users alphabetically by last name ───────────────────────────
  const grouped = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      const lastA = (a.last_name || '').toLowerCase();
      const lastB = (b.last_name || '').toLowerCase();
      if (lastA < lastB) return -1;
      if (lastA > lastB) return 1;
      const firstA = (a.first_name || '').toLowerCase();
      const firstB = (b.first_name || '').toLowerCase();
      return firstA < firstB ? -1 : firstA > firstB ? 1 : 0;
    });

    const groups = {};
    sorted.forEach(c => {
      const initial = (c.last_name || '').charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(initial) ? initial : '#';
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(c);
    });

    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b);
    });

    const result = {};
    sortedKeys.forEach(k => { result[k] = groups[k]; });
    return result;
  }, [users]);

  const availableLetters = Object.keys(grouped);

  useEffect(() => {
    if (search && search.trim().length > 0) {
      setExpandedLetters(new Set(Object.keys(grouped)));
    }
  }, [search, grouped]);

  const toggleLetter = useCallback((letter) => {
    setExpandedLetters(prev => {
      const next = new Set(prev);
      if (next.has(letter)) next.delete(letter);
      else next.add(letter);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedLetters(new Set(Object.keys(grouped)));
  }, [grouped]);

  const collapseAll = useCallback(() => {
    setExpandedLetters(new Set());
  }, []);

  const scrollToLetter = useCallback((letter) => {
    const el = document.getElementById(`user-group-${letter}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedLetters(prev => {
        const next = new Set(prev);
        next.add(letter);
        return next;
      });
    }
  }, []);

  return (
    <>
      {/* ═══ Header Banner ═══ */}
      <div style={{
        background: `linear-gradient(135deg, ${USER_COLOR}15, ${USER_COLOR}08)`,
        borderRadius: 12, padding: '14px 18px', marginBottom: 14,
        boxShadow: neuShadowSm, border: `1px solid ${USER_COLOR}20`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <UserCog size={20} color={USER_COLOR} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#212529' }}>
            ZignaGenix Staff &amp; Team
          </div>
          <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>
            Internal team members, separate from clients and prospects.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <Badge text={`${users.length} user${users.length !== 1 ? 's' : ''}`} color={USER_COLOR} />
      </div>

      {/* ═══ Search Bar ═══ */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#ADB5BD' }} />
          <input
            placeholder="Search users by name, email, phone, or role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 32px 8px 30px', borderRadius: 10, border: 'none',
              background: '#e0e5ec', boxShadow: neuShadowInset, fontSize: 13, outline: 'none',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute', right: 8, top: 7, background: 'none', border: 'none',
                cursor: 'pointer', padding: 2, borderRadius: 4, display: 'flex',
              }}
              title="Clear search"
            >
              <X size={14} color="#ADB5BD" />
            </button>
          )}
        </div>
      </div>

      {/* ═══ Stats + Controls ═══ */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 10, padding: '0 2px',
      }}>
        <div style={{ fontSize: 12, color: '#6C757D' }}>
          <strong style={{ color: '#212529' }}>{users.length}</strong> user{users.length !== 1 ? 's' : ''}
          {search && <span> matching "<em>{search}</em>"</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={expandAll}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: USER_COLOR, fontWeight: 600 }}
          >
            Expand All
          </button>
          <span style={{ color: '#DEE2E6' }}>|</span>
          <button
            onClick={collapseAll}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: USER_COLOR, fontWeight: 600 }}
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* ═══ Quick-Jump Alphabet Bar ═══ */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 12,
        padding: '6px 8px', background: '#F8F9FA', borderRadius: 10, boxShadow: neuShadowSm,
      }}>
        {'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('').map(letter => {
          const has = availableLetters.includes(letter);
          return (
            <button
              key={letter}
              onClick={() => has && scrollToLetter(letter)}
              style={{
                width: 26, height: 26, borderRadius: 6, border: 'none',
                background: has ? (expandedLetters.has(letter) ? USER_COLOR : '#fff') : 'transparent',
                color: has ? (expandedLetters.has(letter) ? '#fff' : '#212529') : '#DEE2E6',
                fontSize: 11, fontWeight: 700, cursor: has ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: has ? neuShadowSm : 'none', transition: 'all .15s ease',
              }}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {/* ═══ Alphabetical Groups ═══ */}
      {users.length === 0 ? (
        <div style={emptyMsg}>
          {search ? 'No users match your search' : 'No users imported yet. Run the GHL Contact Import workflow to import staff/team members.'}
        </div>
      ) : (
        <div ref={scrollContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Object.entries(grouped).map(([letter, groupUsers]) => (
            <UserLetterGroup
              key={letter}
              letter={letter}
              users={groupUsers}
              isExpanded={expandedLetters.has(letter)}
              onToggle={() => toggleLetter(letter)}
              setEditContact={setEditContact}
              setShowEditContact={setShowEditContact}
              setShowSendModal={setShowSendModal}
              setSendChannel={setSendChannel}
              setSendResult={setSendResult}
              setSendPrefill={setSendPrefill}
              loadData={loadData}
            />
          ))}
        </div>
      )}
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Single letter accordion group for users
   ═══════════════════════════════════════════════════════════════════════════ */

function UserLetterGroup({
  letter, users, isExpanded, onToggle,
  setEditContact, setShowEditContact,
  setShowSendModal, setSendChannel, setSendResult, setSendPrefill, loadData,
}) {
  const listRef = useRef(null);
  const { onKeyDown: handleListKeyDown } = useArrowNav(listRef, '[data-user-row]', { horizontal: false, vertical: true });

  return (
    <div
      id={`user-group-${letter}`}
      style={{
        background: '#F8F9FA', borderRadius: 12, boxShadow: neuShadowSm,
        overflow: 'hidden', transition: 'all .2s ease',
        border: isExpanded ? `2px solid ${USER_COLOR}` : '2px solid transparent',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '10px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
          background: isExpanded ? `${USER_COLOR}08` : '#F8F9FA',
          borderRadius: isExpanded ? '10px 10px 0 0' : 10,
          transition: 'background .15s ease',
        }}
        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f0f4f8'; }}
        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = '#F8F9FA'; }}
      >
        <ChevronRight
          size={16}
          color={isExpanded ? USER_COLOR : '#6C757D'}
          style={{
            transition: 'transform .25s ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        />
        <span style={{
          fontSize: 18, fontWeight: 800, color: isExpanded ? USER_COLOR : '#212529',
          width: 24, textAlign: 'center',
        }}>
          {letter}
        </span>
        <div style={{ flex: 1 }} />
        <Badge text={`${users.length} user${users.length !== 1 ? 's' : ''}`} color={isExpanded ? USER_COLOR : '#94a3b8'} />
      </button>

      <div style={{
        maxHeight: isExpanded ? users.length * 60 + 20 : 0,
        opacity: isExpanded ? 1 : 0,
        overflow: 'hidden',
        transition: 'max-height .35s ease, opacity .25s ease',
      }}>
        <div ref={listRef} onKeyDown={handleListKeyDown} style={{ padding: '0 4px 4px' }}>
          {users.map((u, i) => (
            <UserRow
              key={u.id}
              user={u}
              index={i}
              setEditContact={setEditContact}
              setShowEditContact={setShowEditContact}
              setShowSendModal={setShowSendModal}
              setSendChannel={setSendChannel}
              setSendResult={setSendResult}
              setSendPrefill={setSendPrefill}
              loadData={loadData}
            />
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Single user row
   ═══════════════════════════════════════════════════════════════════════════ */

function UserRow({
  user: u, index: i,
  setEditContact, setShowEditContact,
  setShowSendModal, setSendChannel, setSendResult, setSendPrefill, loadData,
}) {
  return (
    <div
      data-user-row
      tabIndex={0}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
        background: i % 2 === 0 ? '#fff' : '#F8F9FA', borderRadius: 8,
        marginBottom: 2, outline: 'none', transition: 'background .1s ease',
      }}
      onFocus={e => { e.currentTarget.style.backgroundColor = '#dce2e8'; }}
      onBlur={e => { e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#fff' : '#F8F9FA'; }}
    >
      {/* Avatar */}
      <div style={{
        width: 34, height: 34, borderRadius: 8,
        background: `${USER_COLOR}18`, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0,
      }}>
        <UserCog size={15} color={USER_COLOR} />
      </div>

      {/* Name + details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#212529' }}>
          {u.last_name ? `${u.last_name}, ${u.first_name}` : u.first_name || 'Unknown'}
        </div>
        <div style={{ fontSize: 11, color: '#6C757D' }}>
          {u.title && <span>{u.title}</span>}
          {u.title && u.organization && <span> &middot; </span>}
          {u.organization && <span>{u.organization}</span>}
        </div>
      </div>

      {/* Email */}
      {u.email && (
        <div style={{ fontSize: 11, color: '#6C757D', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {u.email}
        </div>
      )}

      {/* Phone */}
      {u.phone && (
        <div style={{ fontSize: 11, color: '#6C757D', width: 110, flexShrink: 0 }}>
          {u.phone}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
        {u.phone && (
          <ActionBtn
            icon={<MessageCircle size={13} color="#62BB46" />}
            hoverBg="#dcfce7"
            title={`SMS to ${u.phone}`}
            onClick={() => { setSendPrefill({ contactId: u.id, toAddress: u.phone }); setSendChannel('sms'); setSendResult(null); setShowSendModal(true); }}
          />
        )}
        {u.email && (
          <ActionBtn
            icon={<Mail size={13} color="#2E8B98" />}
            hoverBg="#dbeafe"
            title={`Email ${u.email}`}
            onClick={() => { setSendPrefill({ contactId: u.id, toAddress: u.email }); setSendChannel('email'); setSendResult(null); setShowSendModal(true); }}
          />
        )}
        {u.phone && (
          <ActionBtn
            icon={<PhoneCall size={13} color="#F26522" />}
            hoverBg="#fef3c7"
            title={`Call ${u.phone}`}
            onClick={() => { setSendPrefill({ contactId: u.id, toAddress: u.phone }); setSendChannel('voice'); setSendResult(null); setShowSendModal(true); }}
          />
        )}
        <ActionBtn
          icon={<Edit2 size={13} color="#ADB5BD" />}
          hoverBg="#e0e5ec"
          title="Edit"
          onClick={() => { setEditContact(u); setShowEditContact(true); }}
        />
        <ActionBtn
          icon={<Trash2 size={13} color="#DC3545" />}
          hoverBg="#fee2e2"
          title="Delete"
          onClick={safeAsync(async () => { if (window.confirm(`Delete user ${u.first_name} ${u.last_name}?`)) { await window.zignagenixAPI.deleteCRMContact(u.id); loadData(); } })}
        />
      </div>
    </div>
  );
}


/* ═══ Tiny action button helper ═══ */

function ActionBtn({ icon, hoverBg, title, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: '#F8F9FA', border: 'none', cursor: 'pointer',
        padding: 4, borderRadius: 6, boxShadow: neuShadowSm,
        transition: 'all 0.15s ease', display: 'flex',
      }}
      title={title}
      onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#F8F9FA'; }}
    >
      {icon}
    </button>
  );
}
